<template>
  <div v-if="companyLoaded">
    <div class="flex items-center justify-between px-10 py-8 bg-gray-100 border-b rounded-t-lg">
      <h1 class="text-xl font-semibold tracking-wider text-gray-800 uppercase">
        {{ $t('pages.companies.createChildTitle') }}
      </h1>
      <router-link
        :to="`/companies/${companyId}`"
        class="font-semibold text-gray-600 uppercase hover:text-gray-800"
      >
        {{ $t('common.cancel') }}
      </router-link>
    </div>
    <ValidationObserver
      ref="validationObserver"
      v-slot="{ validated, invalid }"
    >
      <div
        id="wizard"
        class="p-10 pt-0"
      >
        <div
          v-if="validated && invalid"
          class="flex mt-4"
        >
          <div class="w-1/3" />
          <div class="w-2/3">
            <alert type="error">
              {{ $t('forms.validationError') }}
            </alert>
          </div>
        </div>
        <section>
          <div class="w-1/3 description">
            <h1>{{ $t('pages.companies.create.baseData') }}</h1>
            <p>{{ $t('pages.companies.create.onboardingInfo') }}</p>

            <p>
              {{ $t('pages.companies.create.domainInfo') }}
              <span class="font-semibold text-gray-700">
                {{ companyDomain }}</span>
            </p>
          </div>
          <div class="w-2/3">
            <div class="flex flex-wrap mb-6">
              <div class="w-1/2 px-2 form-group">
                <input-text-validated
                  ref="company.slug"
                  v-model="company.slug"
                  input-class="w-full"
                  :label="$t('pages.companies.create.slugLabel')"
                  placeholder="max-makler-immmobilien"
                  rules="required|slug"
                />
              </div>
              <div class="w-1/2 px-2 form-group">
                <input-text-validated
                  ref="company.name"
                  v-model="company.name"
                  input-class="w-full"
                  :label="$t('components.company.baseData.companyName')"
                  placeholder="Max Makler Immobilien"
                  rules="required"
                />
              </div>
            </div>
            <div class="flex flex-wrap mb-6">
              <div
                v-if="countriesForCompany.length > 1"
                class="w-1/2 px-2 form-group"
              >
                <input-select-validated
                  ref="company.country"
                  v-model="company.country"
                  :options="countriesForCompany"
                  :label="$t('pages.companies.create.countryCodeLabel')"
                  rules="required"
                  @change="onCountryChange"
                />
                <p class="pl-1 text-xs text-gray-700">
                  {{ $t('pages.companies.create.cannotChange') }}
                </p>
              </div>
              <Feature
                v-slot="{feature}"
                :feature-slug="featureNames.VARIANT"
              >
                <div
                  v-if="hasMultipleVariants(feature.config.variants)"
                  class="w-1/2 px-2 form-group"
                >
                  <input-select-validated
                    ref="company.variant"
                    v-model="company.variant"
                    :options="variants"
                    :label="$t('modals.company.variant')"
                    rules="required"
                    :disabled="!company.country"
                  />
                  <p
                    v-if="!company.country"
                    class="pl-1 text-xs text-gray-700 "
                  >
                    {{ $t('pages.companies.create.selectCountryFirst') }}
                  </p>
                </div>
              </Feature>
            </div>
          </div>
        </section>
        <section>
          <div class="w-1/3 description">
            <h1>{{ $t('pages.companies.create.billwerk') }}</h1>
            <p>{{ $t('pages.companies.createChild.billwerkComponent') }}</p>
          </div>
          <div class="w-2/3">
            <BillwerkOrder
              create
              child-company
              :order-data="orderData"
              :bookable-sales-plans="[]"
            />
          </div>
        </section>
        <div
          v-if="graphQLErrors.length > 0"
          class="flex mt-4"
        >
          <div class="w-1/3" />
          <div class="w-1/3">
            <alert type="error">
              {{ $t('pages.companies.create.createError') }}
              <ul>
                <li
                  v-for="error in graphQLErrors"
                  :key="error"
                >
                  {{ error }}
                </li>
              </ul>
            </alert>
          </div>
        </div>

        <div class="flex justify-center">
          <div class="pt-8">
            <button
              :disabled="isSubmitting"
              class="btn-primary"
              @click="submit()"
            >
              <span v-if="isSubmitting">{{ $t('pages.companies.create.creating') }}</span>
              <span v-else>{{ $t('pages.companies.createTitle') }}</span>
            </button>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>
<script>
import Alert from '@/components/alert.vue'
import { Feature } from 'feature-config'
import InputTextValidated from '@/components/form/text-validated.vue'
import InputSelectValidated from '@/components/form/select-validated.vue'
import CompanyService from '@/pages/companies/services/CompanyService'
import GET_COMPANY from '@/graphql/GetCompany.gql'
import GET_AVAILABLE_CAMPAIGNS from '@/graphql/AvailableCampaigns.gql'
import { getCountriesForCompany } from '@/config'
import featureMixin from '@/mixins/feature'
import brandingMixin from '@/mixins/branding'
import variantMixin from '@/mixins/variant'
import BillwerkOrder from '@/components/form/billwerk-order.vue'
import CREATE_CHILD_COMPANY from '@/graphql/CreateChildCompany.gql'
export default {
  components: { BillwerkOrder, InputSelectValidated, InputTextValidated, Feature, Alert },
  mixins: [
    brandingMixin,
    featureMixin,
    variantMixin
  ],
  data () {
    return {
      graphQLErrors: [],
      isSubmitting: false,
      countriesForCompany: [],
      variants: [],
      company: {
        slug: '',
        name: '',
        country: '',
        variant: null,
        platform: '',
        salesPerson: { id: '' },
        clientAdvisor: { id: '' },
        productType: '',
        ssoProviders: [],
        components: []
      },
      companyLoaded: false,
      orderData: {
        plan: '',
        planVariant: '',
        discount: '',
        components: [],
        contractId: '',
        customerId: '',
        startDate: null
      }
    }
  },
  computed: {
    availableComponents () {
      return this.$features.feature(this.featureNames.COMPANY).config.availableComponents?.map((type) => ({ value: type, name: type }))
    },
    companyDomain () {
      return this.company?.slug
        ? `${this.company.slug}.${this.landingpageBaseDomain}`
        : `<max-makler-immobilien>.${this.landingpageBaseDomain}`
    },
    companyId () {
      return this.$route.params.id
    }
  },
  watch: {
    '$i18n.locale' () {
      this.setCountriesForCompany()
    },
    parentCompany (newVal) {
      if (newVal) {
        this.company = { ...newVal }
        this.init()
      }
    }
  },
  created () {
    if (CompanyService.getCompany()) {
      this.company = CompanyService.getCompany()
      this.init()
    }
  },
  methods: {
    init () {
      this.variants = this.getMappedVariants()
      this.setCountriesForCompany()
      if (this.variants.length === 1) {
        this.company.variant = this.variants[0].value
      }
      if (this.countriesForCompany.length === 1) {
        this.company.country = this.countriesForCompany[0].value
      }

      this.companyLoaded = true
    },
    setCountriesForCompany () {
      this.countriesForCompany = getCountriesForCompany().filter(({ value }) =>
        this.$features.feature(this.featureNames.COMPANY).config.availableCountryCodes.includes(value)
      )
    },
    async submit () {
      const isValid = await this.$refs.validationObserver.validate()

      if (!isValid) {
        window.scrollTo({ top: 0 })
        return
      }

      try {
        this.isSubmitting = true
        const { country, slug, name, variant, platform, salesPerson, clientAdvisor, productType, ssoProviders, components } = this.company
        const cleanedSsoProviders = (ssoProviders || []).map(({ __typename, ...provider }) => provider)
        const { data: { company } } = await this.$apollo.mutate({
          mutation: CREATE_CHILD_COMPANY,
          variables: {
            input: {
              country,
              slug,
              name,
              variant,
              platform,
              salesPerson: salesPerson?.id,
              clientAdvisor: clientAdvisor?.id,
              productType,
              ssoProviders: cleanedSsoProviders,
              components,
              parentId: this.companyId,
              startDate: this.orderData.startDate
            }
          }
        })

        this.$router.push({
          path: `/companies/${company.id}`
        })

        window.scrollTo({ top: 0 })
        this.isSubmitting = false
      } catch (error) {
        if (error.graphQLErrors) {
          this.graphQLErrors = error.graphQLErrors.map((error) => error.message)
        }
        this.setValidationErrors(error, 'company')
        window.scrollTo({ top: 0 })
        this.isSubmitting = false
      }
    },
    onCountryChange () {
      this.company.variant = this.variants[0].value
    }
  },
  apollo: {
    parentCompany: {
      query: GET_COMPANY,
      variables () {
        return {
          id: this.companyId
        }
      },
      update (data) {
        if (!data || !data.company) {
          return null
        }
        return data.company
      },
      skip () {
        return CompanyService.getCompany() || !this.companyId
      }
    },
    availableCampaigns: {
      query: GET_AVAILABLE_CAMPAIGNS,
      variables () {
        return {
          companyId: this.companyId
        }
      },
      skip () {
        return !this.companyId
      }
    }
  },
  unmounted () {
    CompanyService.clearCompany()
  }
}
</script>
